/**
 * i18n setup — Composition API mode (legacy: false).
 * Spain-first: `es` is the default and fallback locale, `en` is supported.
 * See frontend/docs/adr/0002-i18n-vue-i18n-es-default.md for the resolution order.
 */
import { createI18n } from "vue-i18n";
import en from "./locales/en";
import es from "./locales/es";

export type Locale = "es" | "en";
export const SUPPORTED_LOCALES: Locale[] = ["es", "en"];

const STORAGE_KEY = "zenith_locale";

function isLocale(v: unknown): v is Locale {
  return v === "es" || v === "en";
}

/** Resolution order: saved choice in localStorage -> navigator.language -> es. */
function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // localStorage unavailable (private mode, disabled) — fall through.
  }
  if (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("en")) {
    return "en";
  }
  return "es";
}

export const initialLocale = detectLocale();

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: "es",
  messages: { es, en },
});

/** Persists the locale choice (best effort — wrapped so a disabled/private
 * storage never throws) and updates `<html lang>`. */
export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // best effort only
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

if (typeof document !== "undefined") {
  document.documentElement.lang = initialLocale;
}
